import { useState, useRef } from "react";
import { useGameAuth } from "@/hooks/useGameAuth";
import { GameLayout } from "@/components/game/GameLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Zap } from "lucide-react";
import cyberBg from "@/assets/game/cyber-recent.png";

export default function GameMint() {
  const { session, player, loading, signOut } = useGameAuth();
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [minting, setMinting] = useState(false);

  if (loading) return null;
  if (!session || !player) return <Navigate to="/game" replace />;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max file size is 10MB");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleMint = async () => {
    if (!imageFile) {
      toast.error("Upload an image first");
      return;
    }
    setMinting(true);
    try {
      // Letterbox image into a square (black bg) so the full image is always visible
      const squareBlob = await new Promise<Blob>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const size = Math.max(img.width, img.height);
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject(new Error("Canvas not supported"));
          ctx.fillStyle = "#000000";
          ctx.fillRect(0, 0, size, size);
          const dx = (size - img.width) / 2;
          const dy = (size - img.height) / 2;
          ctx.drawImage(img, dx, dy);
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Encode failed"))), "image/png", 0.95);
        };
        img.onerror = () => reject(new Error("Image load failed"));
        img.src = URL.createObjectURL(imageFile);
      });

      const fileName = `${session.user.id}/game-${Date.now()}-${Math.random().toString(36).slice(2)}.png`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("nft-images")
        .upload(fileName, squareBlob, { cacheControl: "3600", upsert: false, contentType: "image/png" });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("nft-images")
        .getPublicUrl(uploadData.path);

      // Content check: block adult (porn) only. Racy/sexy is allowed.
      const { data: verify, error: verifyError } = await supabase.functions.invoke("game-verify-image", {
        body: { image_url: urlData.publicUrl },
      });
      if (verifyError) {
        console.warn("Verify call failed, allowing mint:", verifyError);
      } else if (verify && verify.approved === false) {
        await supabase.storage.from("nft-images").remove([uploadData.path]);
        throw new Error("Image rejected: explicit adult content is not allowed.");
      }

      const autoName = `NFT-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;

      const { error } = await supabase.from("game_nfts").insert({
        name: autoName,
        description: null,
        image_url: urlData.publicUrl,
        creator_id: player.id,
        owner_id: player.id,
        current_price: 1.25,
        base_price: 1.25,
      });

      if (error) throw error;

      toast.success(`${autoName} minted! 🎨`);
      navigate("/game/marketplace");
    } catch (err: any) {
      toast.error(err.message || "Mint failed");
    } finally {
      setMinting(false);
    }
  };

  return (
    <GameLayout player={player} onSignOut={signOut}>
      <div
        className="cyber-page-bg max-w-lg mx-auto px-4 py-8"
        style={{ ['--cyber-bg' as string]: `url(${cyberBg})` }}
      >
        <Card className="card-game-neon shadow-2xl shadow-purple-500/20">
          <CardContent className="pt-6 space-y-5">
            <div className="text-center space-y-1">
              <h2 className="text-xl font-extrabold text-game-gradient">⚡ Quick Mint</h2>
              <p className="text-sm text-gray-400">Upload an image → Mint instantly for FREE</p>
            </div>

            {/* Image Upload */}
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-purple-500/30 rounded-xl aspect-square flex flex-col items-center justify-center cursor-pointer hover:border-purple-400/60 hover:bg-purple-500/5 transition-all duration-300 overflow-hidden group bg-black"
            >
              {imagePreview ? (
                <img src={imagePreview} alt="Preview" className="w-full h-full object-contain" />
              ) : (
                <div className="text-center space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center mx-auto border border-purple-500/20">
                    <Upload className="w-7 h-7 text-cyan-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-400">Tap to upload image</p>
                  <p className="text-xs text-gray-600">PNG, JPG, GIF — Max 10MB</p>
                </div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />

            <div className="bg-black/60 rounded-lg p-3 text-sm space-y-1 border border-white/5">
              <div className="flex justify-between">
                <span className="text-gray-500">Starting Price:</span>
                <span className="font-bold text-white">$1.25</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Mint Cost:</span>
                <span className="font-bold text-cyan-400">FREE</span>
              </div>
            </div>

            <Button
              onClick={handleMint}
              disabled={minting || !imageFile}
              className="w-full font-bold text-base h-12 bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-500 hover:to-cyan-300 text-black border-0"
            >
              {minting ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-black border-t-transparent" />
                  Minting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Zap className="w-5 h-5" />
                  {imageFile ? "Mint Now" : "Upload Image First"}
                </span>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </GameLayout>
  );
}
